"use client";

import { useEffect } from "react";

/**
 * Trap the hardware/browser back gesture while an unpaid hold is on screen.
 *
 * A back navigation is immediately restored with `history.forward()`, then
 * `onBack` decides what to do (confirm dialog, cancel, plain navigation). This
 * avoids sentinel entries, which otherwise leave a ghost payment page in the
 * history stack after `router.replace()`.
 */
export function createPaymentBackHandler(
  browserHistory: Pick<History, "forward">,
  onBack: () => void,
) {
  let restoring = false;
  return () => {
    if (restoring) {
      restoring = false;
      return;
    }
    restoring = true;
    browserHistory.forward();
    onBack();
  };
}

export function usePaymentBackGuard({
  enabled,
  onBack,
}: {
  enabled: boolean;
  /** Unique per screen, e.g. `payment_guard:${bookingId}`. */
  stateKey: string;
  onBack: () => void;
}) {
  useEffect(() => {
    if (typeof window === "undefined") return;
    if (!enabled) return;

    const onPopState = createPaymentBackHandler(window.history, onBack);

    window.addEventListener("popstate", onPopState);
    return () => window.removeEventListener("popstate", onPopState);
  }, [enabled, onBack]);
}

/**
 * Recover the screen when the user comes back from a bank/UPI app.
 *
 * A Razorpay handoff backgrounds the web view; on return the launch state is
 * stale, so every checkout screen resets its spinner and refetches. Fires on
 * bfcache restore, window focus, and tab visibility.
 */
export function usePaymentResume(onResume: () => void) {
  useEffect(() => {
    if (typeof window === "undefined") return;

    const onPageShow = (event: PageTransitionEvent) => {
      if (event.persisted) onResume();
    };
    const onVisibilityChange = () => {
      if (document.visibilityState === "visible") onResume();
    };

    window.addEventListener("pageshow", onPageShow);
    window.addEventListener("focus", onResume);
    document.addEventListener("visibilitychange", onVisibilityChange);
    return () => {
      window.removeEventListener("pageshow", onPageShow);
      window.removeEventListener("focus", onResume);
      document.removeEventListener("visibilitychange", onVisibilityChange);
    };
  }, [onResume]);
}
